import { useEffect, useRef, useState } from 'react';
import { lessonPlansApi, lessonRunsApi } from '../../services/apiV2';
import { LessonStep } from '../../types/v2';

interface PresentationRun {
  section_id?: number;
  id: number;
  status: string;
  class_current_step_id?: number | null;
  current_step_started_at?: string | null;
  delivery_phase?: string;
  is_test_mode?: boolean;
  live_resource?: Record<string, any>;
}

interface PresentationData {
  run: PresentationRun;
  step: LessonStep | null;
  steps?: LessonStep[];
  signals?: Record<string, any> | null;
  evidence?: Record<string, any> | null;
}

interface PresentationOptions {
  allowControlSummary?: boolean;
  refreshKey?: number;
  lessonPlanId?: number;
  previewStepId?: number | null;
  simulationPreview?: boolean;
}

export const usePresentationStep = (runId?: number, options?: PresentationOptions) => {
  const [data, setData] = useState<PresentationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const inFlightRef = useRef(false);
  const stoppedRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);
  const allowControlSummary = options?.allowControlSummary ?? false;
  const refreshKey = options?.refreshKey ?? 0;
  const lessonPlanId = options?.lessonPlanId;
  const previewStepId = options?.previewStepId ?? null;
  const simulationPreview = options?.simulationPreview ?? false;

  useEffect(() => {
    if (!runId && !lessonPlanId) return undefined;
    stoppedRef.current = false;

    if (!runId && lessonPlanId) {
      let canceled = false;
      const loadLessonPlanPreview = async () => {
        setError(null);
        setLoading(true);
        setUpdating(false);
        try {
          const [planResponse, stepsResponse] = await Promise.all([
            lessonPlansApi.get(lessonPlanId, { audience: 'student' }),
            lessonPlansApi.steps(lessonPlanId, simulationPreview ? { audience: 'student_preview' } : undefined),
          ]);
          if (canceled) return;
          const planData = planResponse.data || {};
          const stepsArr: LessonStep[] = Array.isArray(stepsResponse.data) ? stepsResponse.data : [];
          const orderedSteps = [...stepsArr].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id);
          const selectedStep = (
            previewStepId
              ? orderedSteps.find((item) => item.id === previewStepId)
              : null
          ) || orderedSteps[0] || null;
          setData({
            run: {
              id: 0,
              status: 'active',
              class_current_step_id: selectedStep?.id ?? null,
              current_step_started_at: null,
              delivery_phase: selectedStep?.runner_phase === 'studio' ? 'studio' : 'live',
              is_test_mode: true,
              live_resource: {},
            },
            step: selectedStep,
            steps: orderedSteps,
            current_step_id: selectedStep?.id ?? null,
            class_current_step: selectedStep?.id ?? null,
            lesson_plan: {
              id: planData.id,
              title: planData.title || '',
              hook: planData.hook,
            },
            resources: Array.isArray(planData.resources) ? planData.resources : [],
            current_step_revealed: false,
          } as any);
          setLastUpdatedAt(new Date());
        } catch {
          if (!canceled) {
            setError('Unable to load lesson preview');
          }
        } finally {
          if (!canceled) {
            setLoading(false);
            setUpdating(false);
          }
        }
      };
      void loadLessonPlanPreview();
      return () => {
        canceled = true;
      };
    }
    if (!runId) return undefined;
    const activeRunId = runId;

    const scheduleNext = (status?: string | null) => {
      if (stoppedRef.current) return;
      const nextDelay = status === 'paused' ? 12000 : 3000;
      timeoutRef.current = window.setTimeout(fetchControlSummary, nextDelay);
    };

    const fetchControlSummary = async () => {
      if (stoppedRef.current) return;
      if (document.visibilityState === 'hidden') {
        scheduleNext(data?.run?.status);
        return;
      }
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      const isInitial = !data;
      setError(null);
      setLoading(isInitial);
      setUpdating(!isInitial);

      if (allowControlSummary) {
        try {
          const response = await lessonRunsApi.controlSummary(activeRunId);
          const payload = response.data;
          const nextData: PresentationData = {
            run: {
              id: payload.run?.id,
              section_id: payload.run?.section,
              status: payload.run?.status,
              class_current_step_id: payload.run?.class_current_step_id,
              current_step_started_at: payload.run?.current_step_started_at,
              delivery_phase: payload.run?.delivery_phase,
              is_test_mode: payload.run?.is_test_mode,
              live_resource: payload.run?.live_resource || undefined,
            },
            step: payload.step || null,
            signals: payload.signals || null,
            evidence: payload.evidence || null,
          };
          setData(nextData);
          setLastUpdatedAt(new Date());
          if (payload.run?.status === 'completed') {
            stoppedRef.current = true;
          }
          scheduleNext(payload.run?.status);
          return;
        } catch (err: any) {
          const status = err?.response?.status;
          if (status !== 403 && status !== 404) {
            setError('Unable to load lesson step');
            scheduleNext(data?.run?.status);
            return;
          }
        } finally {
          setLoading(false);
          setUpdating(false);
          inFlightRef.current = false;
        }
      }

      try {
        const runResponse = await lessonRunsApi.get(activeRunId);
        const runData = runResponse.data;
        const stateResponse = await lessonRunsApi.myState(activeRunId);
        const stateData = stateResponse?.data ?? stateResponse;
        const stepsResponse = await lessonPlansApi.steps(
          runData.lesson_plan,
          simulationPreview ? { audience: 'student_preview' } : undefined,
        );
        const stepsArr: LessonStep[] = Array.isArray(stepsResponse.data) ? stepsResponse.data : [];
        const orderedSteps = [...stepsArr].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id);
        const classStepId = stateData?.class_current_step ?? runData?.class_current_step ?? null;
        const activeStudentStepId = stateData?.my_current_step_id ?? classStepId;
        const step = orderedSteps.find((item) => item.id === activeStudentStepId) || orderedSteps[0] || null;
        setData({
          ...(stateData && typeof stateData === 'object' ? stateData : {}),
          run: {
            id: runData.id,
            section_id: runData.section,
            status: stateData?.status || runData.status,
            class_current_step_id: classStepId,
            current_step_started_at: stateData?.current_step_started_at || runData?.current_step_started_at || runData?.live_resource?.current_step_started_at || null,
            delivery_phase: stateData?.delivery_phase || runData?.delivery_phase,
            is_test_mode: Boolean(runData?.is_test_mode),
            live_resource: runData?.live_resource || undefined,
          },
          step,
          steps: orderedSteps,
        });
        setLastUpdatedAt(new Date());
        const resolvedStatus = stateData?.status || runData.status;
        if (resolvedStatus === 'completed') {
          stoppedRef.current = true;
        }
        scheduleNext(resolvedStatus);
      } catch (fallbackError) {
        setError('Unable to load lesson step');
        scheduleNext(data?.run?.status);
      } finally {
        setLoading(false);
        setUpdating(false);
        inFlightRef.current = false;
      }
    };

    fetchControlSummary();
    return () => {
      stoppedRef.current = true;
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [runId, allowControlSummary, refreshKey, lessonPlanId, previewStepId, simulationPreview]);

  return { data, loading, updating, error, lastUpdatedAt };
};
